<script setup lang="ts">
import * as locales from '@nuxt/ui/locale';
import { hexToRgb } from './utils';
import { useGlobalMerchant } from './composables/useGlobalMerchant';
import { useIsMerchantSwitching } from './composables/useMerchantSwitch';
import { useMerchantSlug } from './composables/useMerchantSlug';
import { isSupportedEnvironment } from './utils/isSupportedEnvironment';
import type { ColorScheme } from '~~/models/ColorScheme';

const merchant = useGlobalMerchant();

const appConfig = useAppConfig();

watch(merchant, (_merchant) => {
	appConfig.ui.colors.primary = _merchant ? 'brand' : 'amber';
}, { immediate: true });

const brandColorsStyle = computed(() => {
	if (!merchant.value) return [];

	const hexValues = merchant.value.colorScheme as ColorScheme;
	const rgbValues = Object.entries(hexValues).map(([key, value]) => {
		const rgb = hexToRgb(value);
		if (!rgb) return [key, value];
		return [key, `${rgb.r} ${rgb.g} ${rgb.b}`];
	});

	const cssVariables = rgbValues
		.map(([key, value]) => `--color-brand-${key}: rgb(${value});`)
		.join('\n');

	return [{ id: 'brand-colors', innerHTML: () => `:root { ${cssVariables} }` }];
});

const brandColorsMeta = computed(() => {
	const brandColor = merchant.value ? (merchant.value.colorScheme as ColorScheme)[500] : '#f59e0b';

	return [
		{
			// Chrome, Firefox OS and Opera
			name: 'theme-color',
			content: brandColor,
		},
		{
			// Windows Phone
			name: 'msapplication-navbutton-color',
			content: brandColor,
		},
		{
			name: 'apple-mobile-web-app-capable',
			content: 'yes',
		},
		{
			name: 'apple-mobile-web-app-status-bar-style',
			content: 'black-translucent',
		},
	];
});

const route = useRoute();
const merchantSlugFromRoute = useMerchantSlug(route);

watch(merchantSlugFromRoute, (_slug) => {
	if (!_slug) {
		merchant.value = null;
	}
});

useHead({
	titleTemplate: (titleChunk) => {
		return titleChunk ? `${titleChunk} - Clickfire` : 'Clickfire';
	},
	meta: brandColorsMeta,
	style: brandColorsStyle,
});

const { locale } = useI18n();

const showOutdatedBrowserInfo = ref(!isSupportedEnvironment());

const isSwitchingMerchant = useIsMerchantSwitching();
</script>

<template>
	<UApp
		:locale="locales[locale]"
		:toaster="{
			duration: 10000,
			position: 'top-right'
		}"
	>
		<div>
			<OutdatedBrowser
				v-if="showOutdatedBrowserInfo"
				@dismiss="showOutdatedBrowserInfo = false"
			/>
			<LoadingScreen v-else-if="isSwitchingMerchant" />
			<NuxtLayout v-else>
				<NuxtPage />
			</NuxtLayout>
		</div>
	</UApp>
</template>
